/**
 * Notification Context
 * FindMyLawyer – Lawyer Portal
 *
 * Global state for notifications. Provides:
 * - notifications[], unreadCount, loading, hasMore, cursor
 * - fetchNotifications(filters), loadMore(), markRead(id), markAllRead()
 * - archiveNotification(id), addNotification(n) [for real-time prepend]
 *
 * Real-time strategy:
 * 1. Socket listener on existing socket for `new_notification` events.
 * 2. 60-second polling fallback (no backend-to-backend HTTP calls needed).
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import * as notifService from "../services/notificationService";

const NotificationContext = createContext(null);

export const NotificationProvider = ({ children, socket }) => {
  const [notifications, setNotifications]   = useState([]);
  const [unreadCount,   setUnreadCount]      = useState(0);
  const [loading,       setLoading]          = useState(false);
  const [hasMore,       setHasMore]          = useState(false);
  const [cursor,        setCursor]           = useState(null);
  const [activeFilter,  setActiveFilter]     = useState({ status: null, type: null });
  const pollRef = useRef(null);

  // ── Fetch unread summary (bell badge) ──────────────────────────────────────
  const refreshUnreadCount = useCallback(async () => {
    try {
      const summary = await notifService.fetchUnreadSummary();
      setUnreadCount(summary.count || 0);
    } catch {
      // Silent — badge stays at last known value
    }
  }, []);

  // ── Fetch notifications (replaces current list) ───────────────────────────
  const fetchNotifications = useCallback(async (filters = {}) => {
    setLoading(true);
    setCursor(null);
    setActiveFilter({ status: filters.status || null, type: filters.type || null });
    try {
      const result = await notifService.fetchNotifications({ ...filters, limit: 20 });
      setNotifications(result.notifications || []);
      setHasMore(result.hasMore || false);
      setCursor(result.nextCursor || null);
    } catch (err) {
      console.error("[NotificationContext] fetchNotifications:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  // ── Load more (cursor pagination) ────────────────────────────────────────
  const loadMore = useCallback(async () => {
    if (!hasMore || loading || !cursor) return;
    setLoading(true);
    try {
      const result = await notifService.fetchNotifications({
        ...activeFilter,
        cursor:   cursor.at,
        cursorId: cursor.id,
        limit:    20,
      });
      setNotifications((prev) => {
        const next = [...prev, ...(result.notifications || [])];
        // Cap at 100 in memory to prevent RAM bloat
        return next.length > 100 ? next.slice(0, 100) : next;
      });
      setHasMore(result.hasMore || false);
      setCursor(result.nextCursor || null);
    } catch (err) {
      console.error("[NotificationContext] loadMore:", err);
    } finally {
      setLoading(false);
    }
  }, [hasMore, loading, cursor, activeFilter]);

  // ── Add real-time notification (prepend without refetch) ──────────────────
  const addNotification = useCallback((notification) => {
    setNotifications((prev) => {
      // Deduplicate by id
      if (prev.some((n) => n.id === notification.id)) return prev;
      return [notification, ...prev];
    });
    setUnreadCount((c) => c + 1);
  }, []);

  // ── Mark single as read ──────────────────────────────────────────────────
  const markRead = useCallback(async (id) => {
    // Optimistic update
    const target = notifications.find(n => n.id === id);
    if (!target || target.status === "READ") return;

    setNotifications((prev) =>
      prev.map((n) => (n.id === id ? { ...n, status: "READ" } : n))
    );
    setUnreadCount((c) => Math.max(0, c - 1));

    try {
      await notifService.markAsRead(id);
    } catch (err) {
      // Rollback on failure
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, status: "UNREAD" } : n))
      );
      setUnreadCount((c) => c + 1);
      console.error("[NotificationContext] markRead:", err);
    }
  }, [notifications]);

  // ── Mark all as read ─────────────────────────────────────────────────────
  const markAllRead = useCallback(async () => {
    const unreadIds = notifications.filter(n => n.status === "UNREAD").map(n => n.id);
    if (unreadIds.length === 0) return;

    // Optimistic update
    setNotifications((prev) =>
      prev.map((n) => (n.status === "UNREAD" ? { ...n, status: "READ" } : n))
    );
    const prevCount = unreadCount;
    setUnreadCount(0);

    try {
      await notifService.markAllRead();
    } catch (err) {
      // Rollback on failure
      setNotifications((prev) =>
        prev.map((n) => (unreadIds.includes(n.id) ? { ...n, status: "UNREAD" } : n))
      );
      setUnreadCount(prevCount);
      console.error("[NotificationContext] markAllRead:", err);
    }
  }, [notifications, unreadCount]);

  // ── Archive (soft delete) ────────────────────────────────────────────────
  const archiveNotification = useCallback(async (id) => {
    // Optimistic UI: remove immediately, revert on error
    let removed = null;
    setNotifications((prev) => {
      removed = prev.find((n) => n.id === id);
      return prev.filter((n) => n.id !== id);
    });
    if (removed?.status === "UNREAD") {
      setUnreadCount((c) => Math.max(0, c - 1));
    }

    try {
      await notifService.archiveNotification(id);
    } catch (err) {
      // Revert on failure
      if (removed) {
        setNotifications((prev) => [removed, ...prev]);
        if (removed.status === "UNREAD") setUnreadCount((c) => c + 1);
      }
      console.error("[NotificationContext] archiveNotification:", err);
    }
  }, []);

  // ── Socket listener ───────────────────────────────────────────────────────
  useEffect(() => {
    if (!socket) return;

    const handleNew = (notification) => {
      addNotification(notification);
    };

    socket.on("new_notification", handleNew);
    return () => socket.off("new_notification", handleNew);
  }, [socket, addNotification]);

  // ── Smart 60-second polling fallback ──────────────────────────────────────
  useEffect(() => {
    pollRef.current = setInterval(() => {
      // Only poll if socket is disconnected (saves server load)
      if (!socket || !socket.connected) {
        refreshUnreadCount();
      }
    }, 60_000);
    return () => clearInterval(pollRef.current);
  }, [refreshUnreadCount, socket]);

  // ── Initial load ──────────────────────────────────────────────────────────
  useEffect(() => {
    refreshUnreadCount();
    fetchNotifications();
  }, [refreshUnreadCount, fetchNotifications]);

  const value = {
    notifications,
    unreadCount,
    loading,
    hasMore,
    cursor,
    activeFilter,
    fetchNotifications,
    loadMore,
    addNotification,
    markRead,
    markAllRead,
    archiveNotification,
    refreshUnreadCount,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => {
  const ctx = useContext(NotificationContext);
  if (!ctx) throw new Error("useNotifications must be used within NotificationProvider");
  return ctx;
};
